import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ThemedButton } from '../ThemedButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useWizard } from '../../contexts/WizardContext';

const ORG_TYPES = [
  'Youth Sports League',
  'Club / Travel Team',
  'Tournament or Event Operator',
  'Sports Camp / Clinic',
  'Facility / Venue Operator',
  'Fitness / Training Organization',
  'Franchise or Franchisee'
];

const OrganizationInfo = () => {
  const { formData, updateFormData, nextStep } = useWizard();
  const { register, handleSubmit, control, formState: { errors } } = useForm({
    defaultValues: {
      org_name: formData.org_name || '',
      org_type: formData.org_type || '',
      website: formData.website || '',
      street_address: formData.street_address || '',
      unit: formData.unit || '',
      org_city: formData.org_city || '',
      org_state: formData.org_state || '',
      postal_code: formData.postal_code || '',
    }
  });

  const onSubmit = (data) => {
    updateFormData(data);
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <Label htmlFor="org_name">Organization Name (Full LLC and/or DBA) *</Label>
        <Input
          id="org_name"
          data-testid="org-name-input"
          {...register('org_name', { required: 'Organization name is required' })}
          className="h-12 mt-1.5"
        />
        {errors.org_name && (
          <p className="text-sm text-red-600 mt-1">{errors.org_name.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="org_type">Organization Type *</Label>
        <Controller
          name="org_type"
          control={control}
          rules={{ required: 'Please select an organization type' }}
          render={({ field }) => (
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <SelectTrigger className="h-12 mt-1.5" data-testid="org-type-select">
                <SelectValue placeholder="Select organization type..." />
              </SelectTrigger>
              <SelectContent>
                {ORG_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.org_type && (
          <p className="text-sm text-red-600 mt-1">{errors.org_type.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="website">Website (Optional)</Label>
        <Input
          id="website"
          data-testid="website-input"
          {...register('website')}
          className="h-12 mt-1.5"
          placeholder="https://www.example.com"
        />
      </div>

      <div>
        <Label htmlFor="street_address">Street Address *</Label>
        <Input
          id="street_address"
          data-testid="street-address-input"
          {...register('street_address', { required: 'Street address is required' })}
          className="h-12 mt-1.5"
        />
        {errors.street_address && (
          <p className="text-sm text-red-600 mt-1">{errors.street_address.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="unit">Unit # (Optional)</Label>
        <Input
          id="unit"
          data-testid="unit-input"
          {...register('unit')}
          className="h-12 mt-1.5"
          placeholder="Apt, Suite, Unit, Building, Floor, etc."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="org_city">City *</Label>
          <Input
            id="org_city"
            data-testid="org-city-input"
            {...register('org_city', { required: 'City is required' })}
            className="h-12 mt-1.5"
          />
          {errors.org_city && (
            <p className="text-sm text-red-600 mt-1">{errors.org_city.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="org_state">State *</Label>
          <Input
            id="org_state"
            data-testid="org-state-input"
            {...register('org_state', { required: 'State is required' })}
            className="h-12 mt-1.5"
            placeholder="TX"
            maxLength={2}
          />
          {errors.org_state && (
            <p className="text-sm text-red-600 mt-1">{errors.org_state.message}</p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="postal_code">Postal Code *</Label>
        <Input
          id="postal_code"
          data-testid="postal-code-input"
          {...register('postal_code', { required: 'Postal code is required' })}
          className="h-12 mt-1.5"
        />
        {errors.postal_code && (
          <p className="text-sm text-red-600 mt-1">{errors.postal_code.message}</p>
        )}
      </div>

      <ThemedButton 
        type="submit"
        data-testid="org-info-next-btn"
        variant="wizard" size="full"
      >
        Next: Safety Policies
      </ThemedButton>
    </form>
  );
};

export default OrganizationInfo;
